import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Notifications.css';

/**
 * Уведомления оператору вместо `alert`.
 *
 * `alert` блокирует страницу и не даёт показать два сообщения сразу. Ошибка
 * не гаснет сама — оператор распоряжается реальными деньгами и не должен
 * пропустить отказ, отвернувшись от экрана. Успех и подсказки гаснут.
 */

type NotificationType = 'success' | 'error' | 'info';

export interface Notify {
  success: (text: string) => number;
  error: (text: string) => number;
  info: (text: string) => number;
}

const NotificationsContext = createContext<Notify | null>(null);

const AUTO_DISMISS_MS = 7000;

export const useNotify = (): Notify => {
  const notify = useContext(NotificationsContext);

  if (!notify) {
    throw new Error('useNotify вызван вне NotificationsProvider');
  }

  return notify;
};

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [items, setItems] = useState<
    Array<{ id: number; type: NotificationType; text: string }>
  >([]);
  const lastId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (type: NotificationType, text: string) => {
      lastId.current += 1;
      const id = lastId.current;

      setItems((current) => [...current, { id, type, text }]);

      if (type !== 'error') {
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      }

      return id;
    },
    [dismiss],
  );

  const notify = useMemo<Notify>(
    () => ({
      success: (text) => push('success', text),
      error: (text) => push('error', text),
      info: (text) => push('info', text),
    }),
    [push],
  );

  return (
    <NotificationsContext.Provider value={notify}>
      {children}

      <div className="notifications">
        {items.map(({ id, type, text }) => (
          <div
            key={id}
            className={`notification notification--${type}`}
            role={type === 'error' ? 'alert' : 'status'}
          >
            <span className="notification__text">{text}</span>
            <button
              type="button"
              className="notification__close"
              onClick={() => dismiss(id)}
              aria-label="Закрыть уведомление"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
